import { APP_NAMES } from '@/utils/constants';
import statusTypes from '@/utils/statusTypes';

function filterRecursive(items, query) {
  if (items === null) {
    return false;
  }

  if (Array.isArray(items)) {
    return items.filter(item => filterRecursive(item, query)).length;
  } else if (typeof items === 'object') {
    return Object.values(items).filter(val => filterRecursive(val, query))
      .length;
  } else if (typeof items === 'string') {
    const itemLowerCase = items.toLowerCase();
    const date = new Date(Number(itemLowerCase));
    if (date instanceof Date && !isNaN(date) && date.getFullYear() !== 1970) {
      return date.toISOString().slice(0, 10).includes(query);
    } else {
      return itemLowerCase.toLowerCase().includes(query);
    }
  }

  return false;
}

function filterDate(dateVal, startVal, endVal) {
  const startDate = new Date(startVal);
  const endDate = new Date(endVal);
  const appDate = new Date(dateVal);
  const noStartDate = startVal == null;
  const noEndDate = endVal == null;
  endDate.setHours(23, 59, 59, 999);

  if (noStartDate && noEndDate) {
    return true;
  }
  if (noStartDate) {
    return appDate <= endDate;
  }
  if (noEndDate) {
    return appDate >= startDate;
  }
  return appDate >= startDate && appDate <= endDate;
}

function activeCategories(state, getters, rootState) {
  if (rootState.app.activeApp === APP_NAMES.ANDRAHAND) {
    return state.subletFilterCategories;
  }
  if (
    [APP_NAMES.OVERLATELSER_PRIVATE, APP_NAMES.OVERLATELSER_COMPANY].includes(
      rootState.app.activeApp
    )
  ) {
    return state.ovFilterCategories;
  }
  return state.filterCategories;
}

function activeLandlordUsers(state) {
  const activeLandlordsIds = filterLandlords(state);
  if (!activeLandlordsIds.length) {
    return state.landlordUsers;
  }

  return state.landlordUsers.filter(user =>
    activeLandlordsIds.includes(user.landlordId)
  );
}
function filterCategories(state, getters) {
  return getters.activeCategories
    .filter(cat => cat.selected)
    .map(cat => cat.type);
}

function filterTagsIds(state) {
  return state.filterTags.filter(tag => tag.selected).map(tag => tag.id);
}

function filterLandlordUsers(state) {
  return state.landlordUsers.filter(user => user.selected).map(user => user.id);
}

function filterLandlords(state) {
  return state.landlords
    .filter(landlord => landlord.selected)
    .map(landlord => landlord.id);
}

function filterInput(filter, app) {
  if (filter.length > 0) {
    const query = filter;
    return filterRecursive(app, query.toLowerCase());
  }
  return app;
}

function filterActiveTags(applicationTags, activeTags) {
  if (!activeTags.length) {
    return true;
  }
  return !!activeTags.filter(tagId => applicationTags.includes(tagId)).length;
}

function filterAppsByKey(
  { state, getters },
  key,
  isAhOrOv = false,
  applicationType = null
) {
  const activeCategories = filterCategories(state, getters);
  const activeTags = filterTagsIds(state, getters);
  const activeUsersSet = new Set(filterLandlordUsers(state));
  const activeLandlords = filterLandlords(state);
  const { newCaseFromDate, newCaseToDate, swapFromDate, swapToDate } =
    state.datePickerValue;

  if (!state[key]?.length) {
    return [];
  }

  let statusKey = isAhOrOv ? 'landlordStatus' : 'status';

  return state[key].filter(app => {
    const createdDate = isAhOrOv ? app.sentToHostAt : parseInt(app.sentToHost);
    const assignedLandlordUser = isAhOrOv
      ? app.assignedLandlordUserId
      : app.assignedLandlordUser?.id;
    const secondaryAssignedLandlordUser = isAhOrOv
      ? app.secondaryAssignedLandlordUserId
      : app.secondaryAssignedLandlordUser?.id;
    const assignedManagerUser = isAhOrOv
      ? app.assignedManagerUserId
      : app.assignedManagerUser?.id;
    const secondaryAssignedManagerUser = isAhOrOv
      ? app.secondaryAssignedManagerUserId
      : app.secondaryAssignedManagerUser?.id;

    const assignedIds = [
      assignedLandlordUser ?? null,
      secondaryAssignedLandlordUser ?? null,
      assignedManagerUser ?? null,
      secondaryAssignedManagerUser ?? null
    ];

    const hasNull = activeUsersSet.has(null);
    const nonNullActive = new Set(
      [...activeUsersSet].filter(n => n !== null && n !== undefined)
    );

    const matchUsers =
      activeUsersSet.size === 0 ||
      (hasNull && assignedIds.every(id => id == null)) ||
      (nonNullActive.size > 0 && assignedIds.some(id => nonNullActive.has(id)));

    const landlordId = app.landlordId ?? null;

    return (
      filterActiveTags(app.tags, activeTags) &&
      filterDate(createdDate, newCaseFromDate, newCaseToDate) &&
      filterDate(app.swapDate, swapFromDate, swapToDate) &&
      (activeCategories.length === 0 ||
        activeCategories.includes(app[statusKey])) &&
      matchUsers &&
      (activeLandlords.length === 0 || activeLandlords.includes(landlordId)) &&
      filterInput(state.filter, app) &&
      (applicationType ? app?.applicationType === applicationType : true)
    );
  });
}

function filteredApps(state, getters) {
  return filterAppsByKey({ state, getters }, 'apps');
}

function filteredRemovedApps(state, getters) {
  return filterAppsByKey({ state, getters }, 'removedApps');
}

function filteredAhApps(state, getters) {
  return filterAppsByKey({ state, getters }, 'ahApps', true);
}

function ahFilterMatchCounts(state, getters) {
  if (!state.filter) return { pending: 0, archived: 0 };
  const archivedSet = new Set(getters.ahArchivedStatuses);
  let pending = 0;
  let archived = 0;
  for (const app of getters.filteredAhApps) {
    if (archivedSet.has(app.landlordStatus)) archived++;
    else pending++;
  }
  return { pending, archived };
}

function ovFilterMatchCounts(state, getters) {
  if (!state.filter) return { pending: 0, archived: 0 };
  const archivedSet = new Set(getters.ovArchivedStatuses);
  let pending = 0;
  let archived = 0;
  for (const app of getters.filteredOvApps) {
    if (archivedSet.has(app.landlordStatus)) archived++;
    else pending++;
  }
  return { pending, archived };
}

function baFilterMatchCounts(state, getters) {
  if (!state.filter) return { pending: 0, archived: 0 };
  return {
    pending: getters.filteredApps.length,
    archived: getters.filteredRemovedApps.length
  };
}

function filteredOvApps(state, getters, rootState, rootGetters) {
  const applicationType = rootGetters['app/activeOvApplicationType'];
  return filterAppsByKey({ state, getters }, 'ovApps', true, applicationType);
}

function activeFilters(state, getters) {
  const { newCaseFromDate, newCaseToDate, swapFromDate, swapToDate } =
    state.datePickerValue;
  return [
    !!state.filterTags.find(tag => tag.selected),
    !!newCaseFromDate || !!newCaseToDate,
    !!swapFromDate || !!swapToDate,
    !!getters.activeCategories.find(tag => tag.selected),
    !!state.landlordUsers.find(user => user.selected)
  ].filter(val => val).length;
}

function ahAppsChains(state) {
  const chains = {};
  if (!state.ahApps) {
    return chains;
  }
  state.ahApps
    .filter(app => app.latestVersion === 1)
    .forEach(app => {
      //const parentApp = state.ahApps.find(x => x.id === app.parentId);

      if (app.parentId /*&& parentApp*/) {
        chains[app.id] = app.parentId;
        chains[app.parentId] = app.id;
      }
    });
  return chains;
}

function ahPendingStatuses() {
  return [statusTypes.INCOMING, statusTypes.READY, statusTypes.APPROVED];
}
function ahArchivedStatuses() {
  return [
    statusTypes.CLOSED,
    statusTypes.REJECTED,
    statusTypes.ABORTED,
    statusTypes.REMOVED,
    statusTypes.PENDING_REMOVE
  ];
}

function ovArchivedStatuses() {
  return [
    statusTypes.CLOSED,
    statusTypes.REJECTED,
    statusTypes.ABORTED,
    statusTypes.REMOVED,
    statusTypes.PENDING_REMOVE
  ];
}

function ovPendingStatuses() {
  return [statusTypes.INCOMING, statusTypes.READY, statusTypes.APPROVED];
}

export default {
  filteredApps,
  filteredRemovedApps,
  filteredAhApps,
  ahFilterMatchCounts,
  ovFilterMatchCounts,
  baFilterMatchCounts,
  filteredOvApps,
  activeCategories,
  activeFilters,
  ahAppsChains,
  ahPendingStatuses,
  ahArchivedStatuses,
  ovArchivedStatuses,
  ovPendingStatuses,
  activeLandlordUsers
};
